import { useState, useEffect, useCallback, useRef } from 'react';
import { getAllUsers, AppUser } from '../services/api/user';
import { getOrganizations, Organization } from '../services/api/organizations';
import { getBuildings, Building } from '../services/api/buildings';

// Global cache for buildings to avoid duplicate API calls
declare global {
  var __buildingsGlobalCache: {
    data: Building[] | null;
    timestamp: number;
  } | undefined;
}

interface HomePageData {
  users: AppUser[];
  organizations: Organization[];
  buildings: Building[];
}

interface HomePageStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  totalOrganizations: number;
  totalBuildings: number;
}


let globalDataCache: {
  data: HomePageData | null;
  loading: boolean;
  error: string | null;
  promise: Promise<HomePageData> | null;
  userRole: string | null;
} = {
  data: null,
  loading: false,
  error: null,
  promise: null,
  userRole: null
};

// Global listeners for cache updates
const listeners = new Set<() => void>();

const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

export const useHomePageData = (userRole: 'admin' | 'user' | null) => {
  const [, forceUpdate] = useState({});
  const listenerRef = useRef<(() => void) | undefined>(undefined);

  // Force re-render when cache updates
  const handleCacheUpdate = useCallback(() => {
    forceUpdate({});
  }, []);

  useEffect(() => {
    // Subscribe to cache updates
    listenerRef.current = handleCacheUpdate;
    listeners.add(handleCacheUpdate);

    return () => {
      // Cleanup subscription
      if (listenerRef.current) {
        listeners.delete(listenerRef.current);
      }
    };
  }, [handleCacheUpdate]);

  const fetchData = useCallback(async (role: 'admin' | 'user'): Promise<HomePageData> => {
    // If we already have data for this role, return it
    if (globalDataCache.data && globalDataCache.userRole === role) {
      console.log(`useHomePageData: Using cached data for role: ${role}`);
      return globalDataCache.data;
    }

    // If we're already loading data for this role, wait for it
    if (globalDataCache.loading && globalDataCache.userRole === role && globalDataCache.promise) {
      console.log(`useHomePageData: Waiting for existing promise for role: ${role}`);
      return globalDataCache.promise;
    }

    // Clear previous data if role changed
    if (globalDataCache.userRole !== role) {
      globalDataCache.data = null;
      globalDataCache.error = null;
    }

    // Start loading
    globalDataCache.loading = true;
    globalDataCache.userRole = role;
    globalDataCache.error = null;
    notifyListeners();

    const fetchPromise = async (): Promise<HomePageData> => {
      try {
        console.log(`useHomePageData: Fetching data for role: ${role}`);
        if (role === 'admin') {
          // Admin: fetch all three APIs
          console.log('useHomePageData: Fetching users, organizations, and buildings for admin');
          const [users, organizations, buildings] = await Promise.all([
            getAllUsers(),
            getOrganizations(),
            getBuildings()
          ]);
          
          // Update global buildings cache
          global.__buildingsGlobalCache = {
            data: buildings,
            timestamp: Date.now()
          };
          
          return { users, organizations, buildings };
        } else {
          // Regular user: only fetch buildings
          console.log('useHomePageData: Fetching only buildings for regular user');
          const [buildings] = await Promise.all([
            getBuildings()
          ]);
          
          // Update global buildings cache
          global.__buildingsGlobalCache = {
            data: buildings,
            timestamp: Date.now()
          };
          
          return { users: [], organizations: [], buildings };
        }
      } catch (error) {
        globalDataCache.loading = false;
        globalDataCache.error = error instanceof Error ? error.message : 'Failed to load data';
        globalDataCache.promise = null;
        notifyListeners();
        throw error;
      }
    };

    globalDataCache.promise = fetchPromise();

    try {
      const result = await globalDataCache.promise;
      globalDataCache.data = result;
      globalDataCache.loading = false;
      globalDataCache.error = null;
      notifyListeners();
      return result;
    } catch (error) {
      // Error handling already done in fetchPromise
      throw error;
    }
  }, []);

  const refetch = useCallback(async () => {
    if (!userRole) return;
    
    // Clear cache to force refetch
    globalDataCache.data = null;
    globalDataCache.promise = null;
    
    try {
      await fetchData(userRole);
    } catch (error) {
      console.error('Failed to refetch data:', error);
    }
  }, [userRole, fetchData]);

  // Start fetching data when userRole is available
  useEffect(() => {
    if (userRole && (!globalDataCache.data || globalDataCache.userRole !== userRole)) {
      fetchData(userRole).catch(error => {
        console.error('Failed to fetch homepage data:', error);
      });
    }
  }, [userRole, fetchData]);

  // Calculate stats from current data
  const stats: HomePageStats = {
    totalUsers: globalDataCache.data?.users.length || 0,
    activeUsers: globalDataCache.data?.users.filter(u => u.active).length || 0,
    inactiveUsers: globalDataCache.data?.users.filter(u => !u.active).length || 0,
    totalOrganizations: globalDataCache.data?.organizations.length || 0,
    totalBuildings: globalDataCache.data?.buildings.length || 0
  };

  return {
    data: globalDataCache.data,
    stats,
    loading: globalDataCache.loading,
    error: globalDataCache.error,
    refetch
  };
};
